import { motion } from "framer-motion";
import { useState } from "react";
import SiteNavbar from "../components/SiteNavbar";
import { NavLink } from "react-router-dom";
import heroPricingLight from "../assets/images/hero-pricing-light-pexels.jpeg";
import heroPricingDark from "../assets/images/hero-pricing-dark-pexels.jpeg";

type Tier = {
  name: string;
  monthly: number | null; // monthly price
  yearly?: number | null; // price when billed annually (per month)
  description: string;
  features: string[];
  cta: string;
  highlight?: boolean;
};

const tiers: Tier[] = [
  {
    name: "Free trial",
    monthly: 0,
    description: "Grade up to 50 assignments (≤1,000 words) every month — perfect to test the waters.",
    features: [
      "50 assignments / month",
      "Secure class attendance",
      "Session interactivity (timers, quizzes)",
    ],
    cta: "Start free trial",
  },
  {
    name: "Academic",
    monthly: 70,
    yearly: 65,
    description: "For individual educators who need serious throughput without the admin overhead.",
    features: [
      "0.5 M tokens / month",
      "Unlimited institutions & courses",
      "Unlimited sessions",
      "Everything in Free plus extras",
    ],
    cta: "Subscribe now",
    highlight: true,
  },
  {
    name: "Department / Team",
    monthly: 560,
    yearly: 520,
    description: "Collaborative tools and higher token limits for departments and schools.",
    features: [
      "Up to 10 educators included",
      "Additional users AUD 60 / month",
      "5 M tokens / month",
      "Department, course & session notes",
      "Team feedback libraries & analytics",
      "Everything in Academic",
    ],
    cta: "Contact sales",
  },
];

export default function PricingPage() {
  // Toggle removed — only one pilot tier for now
  const [annual, setAnnual] = useState(false);

  return (
    <div className="flex min-h-screen flex-col bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-50">
      <SiteNavbar />

      {/* Hero banner */}
      <div className="relative h-64 w-full md:h-80">
        <img
          src={heroPricingLight}
          alt="Bright open office space"
          className="theme-light-only absolute inset-0 h-full w-full object-cover"
        />
        <img
          src={heroPricingDark}
          alt="Professional working late in modern office"
          className="theme-dark-only absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-white/40 to-white dark:from-gray-900/70 dark:via-gray-900/40 dark:to-gray-900" />
        <div className="relative z-10 flex h-full flex-col items-center justify-center px-6 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-gray-900 dark:text-gray-50 md:text-5xl">
            Simple, educator-friendly pricing
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-gray-700 dark:text-gray-300">
            No hidden fees. Cancel anytime. Introductory pricing ends 31&nbsp;July&nbsp;2024.
          </p>
        </div>

        {/* Credits – light */}
        <p className="absolute bottom-2 right-4 text-xs text-gray-500 dark:hidden">
          Photo by{' '}
          <a href="https://www.pexels.com/@fourmedia" target="_blank" rel="noreferrer" className="underline">
            FOURMEDIA
          </a>{' '}
          on{' '}
          <a href="https://www.pexels.com" target="_blank" rel="noreferrer" className="underline">
            Pexels
          </a>
        </p>

        {/* Credits – dark */}
        <p className="absolute bottom-2 right-4 hidden text-xs text-gray-400 dark:block">
          Photo by{' '}
          <a href="https://www.pexels.com/@cottonbro" target="_blank" rel="noreferrer" className="underline">
            cottonbro studio
          </a>{' '}
          on{' '}
          <a href="https://www.pexels.com" target="_blank" rel="noreferrer" className="underline">
            Pexels
          </a>
        </p>
      </div>

      <section className="mx-auto w-full max-w-7xl flex-1 px-6 py-24">
        <h1 className="text-center text-4xl font-extrabold tracking-tight text-gray-900 dark:text-gray-50 md:text-5xl">
          Simple, transparent pricing
        </h1>
        <p className="mt-4 text-center text-lg text-gray-600 dark:text-gray-400">
          No hidden fees. Upgrade, downgrade or cancel anytime.
        </p>

        {/* Billing toggle */}
        <div className="mx-auto mt-8 flex w-fit items-center gap-4 rounded-full border border-gray-300 dark:border-gray-600 p-2 bg-white dark:bg-gray-800">
          <button
            onClick={() => setAnnual(false)}
            className={
              (!annual ? "bg-primary text-white" : "text-gray-700 dark:text-gray-200") +
              " rounded-full px-4 py-1 text-sm font-medium transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
            }
          >
            Monthly
          </button>
          <button
            onClick={() => setAnnual(true)}
            className={
              (annual ? "bg-primary text-white" : "text-gray-700 dark:text-gray-200") +
              " rounded-full px-4 py-1 text-sm font-medium transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
            }
          >
            Yearly <span className="ml-1 text-xs">(save ~7%)</span>
          </button>
        </div>

        {/* Plan cards */}

        <div className="mt-16 grid gap-8 md:grid-cols-3">
          {tiers.map((tier) => {
            const selectedPrice = annual ? tier.yearly ?? tier.monthly : tier.monthly;
            const priceLabel =
              selectedPrice === 0
                ? "Free"
                : selectedPrice === null
                ? "Contact us"
                : `AUD $${selectedPrice}`;
            const isPopular = tier.highlight ?? false;

            return (
              <motion.div
                key={tier.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.4 }}
                className={
                  "relative flex flex-col rounded-lg border bg-white dark:bg-gray-800 p-8 shadow-sm" +
                  (isPopular ? " ring-2 ring-primary" : " border-gray-200 dark:border-gray-600")
                }
              >
                {isPopular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-0.5 text-xs font-medium text-white">
                    Most popular
                  </span>
                )}

                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-50">{tier.name}</h2>
                <p className="mt-4 text-4xl font-extrabold text-gray-900 dark:text-gray-50">
                  {priceLabel}
                  {selectedPrice !== null && selectedPrice !== 0 && (
                    <span className="text-base font-medium text-gray-600 dark:text-gray-400"> /mo</span>
                  )}
                </p>
                <p className="mt-2 text-gray-600 dark:text-gray-400">{tier.description}</p>

                <ul className="mt-6 space-y-2 text-sm text-gray-700 dark:text-gray-300">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <span className="mt-0.5 h-2 w-2 rounded-full bg-primary" /> {f}
                    </li>
                  ))}
                </ul>

                <NavLink
                  to="/signup"
                  className="mt-8 inline-block rounded-md bg-primary px-4 py-2 text-center text-sm font-semibold text-white shadow hover:bg-primary-dark focus:outline-hidden focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-offset-gray-900"
                >
                  {tier.cta}
                </NavLink>
              </motion.div>
            );
          })}
        </div>

      {/* Inclusions */}
      <div className="mx-auto mt-16 max-w-3xl rounded-lg bg-gray-50 dark:bg-gray-800 p-8 shadow-sm">
        <h3 className="mb-4 text-xl font-semibold text-gray-900 dark:text-gray-50 text-center">Every plan includes</h3>
        <ul className="grid gap-3 text-sm text-gray-700 dark:text-gray-300 md:grid-cols-2">
          <li className="flex items-start gap-2"><span className="mt-1 h-2 w-2 rounded-full bg-primary" /> Secure class attendance</li>
          <li className="flex items-start gap-2"><span className="mt-1 h-2 w-2 rounded-full bg-primary" /> Live session timers &amp; countdowns</li>
          <li className="flex items-start gap-2"><span className="mt-1 h-2 w-2 rounded-full bg-primary" /> In-session quizzes &amp; polls</li>
          <li className="flex items-start gap-2"><span className="mt-1 h-2 w-2 rounded-full bg-primary" /> Privacy-first analytics</li>
        </ul>
      </div>

      {/* Token explainer */}
      <div className="mx-auto mt-20 max-w-3xl text-center">
        <h3 className="mb-3 text-2xl font-semibold text-gray-900 dark:text-gray-50">How tokens work</h3>
        <p className="text-gray-700 dark:text-gray-300">
          We use the same token system as OpenAI: roughly <strong>4 tokens per word</strong> on average. A typical
          1 000-word assignment therefore uses about <strong>4 500 tokens</strong> for full rubric mapping, reference
          validation and feedback generation. Your monthly token allowance covers <em>all</em> processing — no surprise
          overage fees.
        </p>
      </div>

      {/* Feature comparison grid */}
      <div className="mx-auto mt-20 w-full overflow-x-auto">
        <table className="mx-auto w-full max-w-5xl border-collapse text-left text-sm">
          <thead>
            <tr>
              <th className="px-4 py-3 text-gray-900 dark:text-gray-50">Feature</th>
              {tiers.map((t) => (
                <th key={t.name} className="px-4 py-3 text-center text-gray-900 dark:text-gray-50">
                  {t.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {[
              { name: 'Institutions', values: ['1', 'Unlimited', 'Unlimited'] },
              { name: 'Courses', values: ['1', 'Unlimited', 'Unlimited'] },
              { name: 'Groups', values: ['5', 'Unlimited', 'Unlimited'] },
              { name: 'Assessments per course', values: ['1', 'Unlimited', 'Unlimited'] },
              { name: 'Reference validation', values: ['✓', '✓', '✓'] },
              { name: 'Rubric mapping', values: ['✓', '✓', '✓'] },
              { name: 'AI writing detection', values: ['—', '✓', '✓'] },
              { name: 'Course templates / duplication', values: ['—', '✓', '✓'] },
              { name: 'Educator↔︎educator secure notes', values: ['—', '—', '✓'] },
              { name: 'Student progress dashboard', values: ['—', '—', '✓'] },
              { name: 'Assessment reminders for students', values: ['—', '—', '✓'] },
            ].map((row) => (
              <tr key={row.name}>
                <td className="whitespace-nowrap px-4 py-3 text-gray-700 dark:text-gray-300">{row.name}</td>
                {row.values.map((v, idx) => (
                  <td key={idx} className="px-4 py-3 text-center text-gray-900 dark:text-gray-50">
                    {v === '✓' ? (
                      <span className="text-primary">✓</span>
                    ) : v === '—' ? (
                      <span className="text-gray-400">—</span>
                    ) : (
                      v
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

        {/* Guarantee removed – pilot is free */}
      </section>

      {/* CTA */}
      <section className="bg-primary py-24 text-center text-white">
        <h2 className="mb-6 text-3xl font-bold md:text-4xl">Ready to save hours on marking?</h2>
        <NavLink
          to="/signup"
          className="inline-block rounded-md bg-white px-10 py-4 text-base font-semibold text-primary shadow-xs hover:bg-gray-100"
        >
          Start free trial
        </NavLink>
      </section>

      <footer className="border-t bg-gray-50 py-8 text-center text-sm text-gray-500">
        © {new Date().getFullYear()} ItsAcademic.ai — created on Gadigal and Wangal Country by the team at Aperim Pty
        Ltd.
      </footer>
    </div>
  );
}
